import {Controller} from '@stubhub/react-store-provider';

import SearchInventoryV2 from './models/listing_search_inventory_v2';
import {
  availableSectionsSelector,
  dataSelector,
  deviceTypeSelector,
  filterChangedSelector,
  hoveredListingSectionIdSelector,
  hoveredListingZoneIdSelector,
  listingSelectedSelector,
  previousBYOQuantitySelector,
  previousListingSelectedSelector,
  rowListingSelectedSelector,
  sectionColorsSelector,
  storeIdSelector,
} from './selectors';

const SearchInventoryV2Model = SearchInventoryV2();

// TODO: async N listing count and repeat until all inventory is fetched 5kb per 100  so 4000 listings would be 200kb
function getListings(eventId, options, reset, lang, logger) {
  return SearchInventoryV2Model.get({
    headers: _getFetchRequestHeaders(lang),
    eventId,
    logger,
    ...options,
  });
}

// Goal no duplicates and super simple code
function Meta() {
  const memory = {};
  this.insert = (value) => {
    memory[value] = 1;
  };
  this.toArray = () => Object.keys(memory);
}

function listingsToSectionRowSeatDS(listings = [], existingStructure) {
  let sections = {
    meta: {
      availableSeats: [],
    },
  };
  const mapSectionRowSeatIdsToListingId = {};

  if (existingStructure) {
    sections = existingStructure;
  }
  const constructMeta = new Meta();
  for (let i = 0; i < listings.length; i++) {
    const {sectionId, seats} = listings[i];
    const [{listingId}] = seats;
    mapSectionRowSeatIdsToListingId[listingId] = {};

    for (let r = 0; r < seats.length; r++) {
      const {row, seatNumber} = seats[r];
      if (!sections[sectionId]) {
        sections[sectionId] = {};
      }
      if (!sections[sectionId][row]) {
        sections[sectionId][row] = {};
      }
      constructMeta.insert(`${sectionId}-${row}-${seatNumber}`);
      mapSectionRowSeatIdsToListingId[listingId][`${sectionId}-${row}-${seatNumber}`] = true;
      if (!sections[sectionId][row][seatNumber]) {
        sections[sectionId][row][seatNumber] = listings[i]; // Wasting memory for lookup speed
      }
    }
  }
  sections.meta.availableSeats = constructMeta.toArray();

  return {
    mapSectionRowSeatIdsToListingId,
    sections,
  };
}

function listingsToSectionRowDS(listings = [], existingStructure) {
  let sections = {
    rowMeta: {
      availableRows: [],
    },
  };

  if (existingStructure) {
    sections = existingStructure;
  }
  const constructMetaRows = new Meta();
  for (let i = 0; i < listings.length; i++) {
    const {sectionId, seats} = listings[i];
    const [{row, listingId}] = seats;
    if (!sections[sectionId]) {
      sections[sectionId] = {};
    }
    if (!sections[sectionId][row]) {
      sections[sectionId][row] = {};
    }
    constructMetaRows.insert(`${sectionId}-${row}`);
    sections[sectionId][row][listingId] = listings[i];
  }
  sections.rowMeta.availableRows = constructMetaRows.toArray();

  return sections;
}

function joinList(list) {
  if (list && Array.isArray(list)) {
    return list.length > 1 && list.join();
  }

  return list;
}

function parseSearchInventoryV2Parameters(options) {
  const {
    sort = 'price asc, value desc',
    priceWithFees,
    listingCategory,
    excludeCategory: excludeListingAttributeCategoryList,
    deliveryType,
    quantity,
    sliderMin = [],
    sliderMax = [],
    sectionIdList,
    shstore,
  } = options;
  const sortList = joinList(sort);
  const listingAttributeCategoryList = joinList(listingCategory);
  const deliveryTypeList = joinList(deliveryType);
  const [pricemin] = sliderMin;
  const [pricemax] = sliderMax;

  let priceType = 'nonBundledPrice';
  if (priceWithFees) {
    priceType = 'bundledPrice';
  }

  let parsedQuantity = quantity;
  if (quantity === 0) {
    parsedQuantity = null;
  }
  if (quantity === 5) {
    parsedQuantity = '>5';
  }

  return {
    quantity: parsedQuantity,
    sort: sortList,
    priceType,
    listingAttributeCategoryList,
    excludeListingAttributeCategoryList,
    deliveryTypeList,
    pricemin,
    pricemax,
    sectionIdList: sectionIdList || null,
    shstore,
  };
}

/*
 * Need to make map sectionStats be independent of ticke listing's
 * because it is not required to be filtered by rows
 * set rows = 0 because listings are not required for this use case
 */
function fetchSectionStats(eventId, options = {}, reset) {
  return async (dispatch, getState, {logger}) => {
    try {
      const {lang, offset: currentOffset} = getState().eventPageRoyalSeatmap || {};
      dispatch({
        type: 'SEATMAP:SECTIONSTATS_LOADING',
        sectionStatsLoading: true,
      });
      const parsedOptions = parseSearchInventoryV2Parameters(options) || {};
      const {sectionStats, zoneStats} =
        (await getListings(
          eventId,
          {
            ...parsedOptions,
            offset: currentOffset,
            sectionIdList: null,
            rows: 0,
          },
          reset,
          lang,
          logger
        )) || [];

      return dispatch({
        type: 'SEATMAP:SECTIONSTATS_LOADED',
        mapSectionStats: sectionStats,
        mapZoneStats: zoneStats,
        sectionStatsLoading: false,
      });
    } catch (e) {
      return dispatch({
        type: 'SEATMAP:SECTIONSTATS_ERROR',
        error: e,
        sectionStatsLoading: false,
      });
    }
  };
}

const GET_LISTINGS_OFFSET = 100;

/**
 *
 * @reference: https://wiki.stubcorp.com/pages/viewpage.action?pageId=31261000
 * @param eventId
 * @param options
 * @param reset
 * @returns {Function}
 */
function fetchSeatInventoryListings(eventId, options = {}, reset) {
  return async (dispatch, getState, {logger}) => {
    try {
      const {lang, offset: currentOffset} = getState().eventPageRoyalSeatmap || {};

      dispatch({type: 'SEATMAP:LISTING_LOADING', listingLoading: true});
      let parsedOptions = {};

      parsedOptions = parseSearchInventoryV2Parameters(options);

      /*
       * Since one api call only returns at most 100 listings, we need to know the
       * total number of listings and use Promise.all to retrive all of them.
       */
      const {totalListings} = await getListings(
        eventId,
        {
          ...parsedOptions,
          offset: currentOffset,
          rows: 0,
          sectionStats: false,
        },
        reset,
        lang,
        logger
      );

      const promiseArray = [];

      for (let i = 0; i < totalListings; i += GET_LISTINGS_OFFSET) {
        promiseArray.push(
          getListings(
            eventId,
            {
              ...parsedOptions,
              start: i,
              sectionStats: false,
            },
            reset,
            lang,
            logger
          )
        );
      }

      return Promise.all(promiseArray)
        .then((results) => {
          let combinedListings = [];
          for (let i = 0; i < results.length; i++) {
            combinedListings = [...combinedListings, ...results[i].listing];
          }

          return combinedListings;
        })
        .then((combinedListings) => {
          const {event} = getState();
          if (event && event.listingSelected) {
            combinedListings.push(event.listingSelected);
          }
          if (event && event.previousListingSelected) {
            combinedListings.push(event.previousListingSelected);
          }

          const listingsDSResult = listingsToSectionRowSeatDS(combinedListings);
          const listingsRowDSResult = listingsToSectionRowDS(combinedListings);
          const {sections = {}, mapSectionRowSeatIdsToListingId = {}} = listingsDSResult;
          const {meta = {availableSeats: []}} = sections;
          const {rowMeta = {availableRows: []}} = listingsRowDSResult;
          const {availableSeats} = meta;
          const {availableRows} = rowMeta;

          return dispatch({
            type: 'SEATMAP:LISTING_LOADED',
            eventId,
            listingLoading: false,
            cumulativeListings: combinedListings,
            listingsDS: sections || {},
            listingsRowDSResult: listingsRowDSResult || {},
            availableSeats,
            availableRows,
            mapSectionRowSeatIdsToListingId,
            quantity: options.quantity,
          });
        })
        .catch((error) => {
          dispatch({
            type: 'SEATMAP:LISTING_ERROR',
            listingLoading: false,
            error,
          });
        });
    } catch (error) {
      dispatch({
        type: 'SEATMAP:LISTING_ERROR',
        listingLoading: false,
        error,
      });
    }
  };
}

function setPreviousListingSelected(listing, byo_quantity) {
  return (dispatch) => {
    if (listing) {
      dispatch({
        type: 'SEATMAP:LISTING_SELECTED',
        previousListingSelected: listing,
        previousBYOQuantity: byo_quantity,
      });
    } else if (!listing) {
      dispatch({
        type: 'SEATMAP:LISTING_SELECTED',
        previousListingSelected: null,
        previousBYOQuantity: null,
      });
    }
  };
}

function setListingSelected(listing) {
  return (dispatch, getState, {logger}) => {
    dispatchListingSelected(listing, dispatch, getState, {logger});
  };
}

function fetchSelectedListing(eventId, options = {}, reset) {
  return (dispatch, getState, {logger}) => {
    try {
      const {lang, offset: currentOffset} = getState().eventPageRoyalSeatmap || {};

      const {listingId} = options;
      let parsedOptions = {};
      if (listingId) {
        parsedOptions = Object.assign(options, {listingIds: listingId});
      }
      const requestPromise = getListings(eventId, {...parsedOptions, offset: currentOffset}, reset, lang, logger);

      requestPromise
        .then(({listing: listings}) => {
          const [listing] = listings;

          return dispatchListingSelected(listing, dispatch, getState, {logger});
        })
        .catch(() => {
          dispatch({
            type: 'EVENT:LISTING:SELECTED:ERROR',
            listingSelected: null,
          });
        });
    } catch (e) {
      dispatch({
        type: 'EVENT:LISTING:SELECTED:ERROR',
        listingSelected: null,
        error: e,
      });
    }
  };
}

function _getFetchRequestHeaders(language) {
  const ret = {
    Authorization: process.env.REACT_APP_API_SECRET,
  };
  if (language) {
    ret['Accept-Language'] = language;
  }

  return ret;
}

function dispatchListingSelected(listing, dispatch) {
  if (listing) {
    dispatch({
      type: 'EVENT:LISTING:SELECTED',
      listingSelected: listing,
    });
  } else if (!listing) {
    dispatch({
      type: 'EVENT:LISTING:SELECTED',
      listingSelected: null,
    });
  }
}

function dispatchFilterChangedHandled() {
  return (dispatch) => {
    return dispatch({
      type: 'EVENT:FILTERNOTCHANGED',
      filterChanged: false,
    });
  };
}

const listingSelectedSourceSelector = (listingSelected, previousListingSelected) => {
  let result = 'none';

  // PreviousListingSelected only set from map interaction
  if (previousListingSelected) {
    result = 'map';
  } else if (listingSelected) {
    result = 'ticketlist';
  }

  return result;
};

const controller = new Controller({
  namespace: 'eventPageRoyalSeatmap',
  mapStateToProps(state, props) {
    const localState = this.getLocalState(state) || {};
    const listingSelected = listingSelectedSelector(state);
    const previousListingSelected = previousListingSelectedSelector(state);
    const rowListingSelected = rowListingSelectedSelector(state);
    const {mapType, listingInfo} = props;

    return {
      listingSelected,
      rowListingSelected,
      sectionIds: localState.sectionIds,
      listings: localState.cumulativeListings,
      listingsDS: localState.listingsDS,
      listingsRowDSResult: localState.listingsRowDSResult,
      listingLoading: localState.listingLoading,
      availableSeats: localState.availableSeats,
      availableRows: localState.availableRows,
      hoveredId: localState.sectionId,
      deviceType: deviceTypeSelector(state),
      sectionsRowsSeats: localState.sectionsRowsSeats,
      mapState: localState.mapState,
      listingSelectedSource: listingSelectedSourceSelector(listingSelected, previousListingSelected),
      previousListingSelected,
      previousBYOQuantity: previousBYOQuantitySelector(state),
      ...dataSelector(state),
      filterChanged: filterChangedSelector(state),
      mapSectionStats: localState.mapSectionStats || [],
      mapZoneStats: localState.mapZoneStats || [],
      ...availableSectionsSelector(state, {mapType, listingInfo}),
      sectionColors: sectionColorsSelector(state),
      mapSectionRowSeatIdsToListingId: localState.mapSectionRowSeatIdsToListingId,
      hoveredListingSid: hoveredListingSectionIdSelector(state),
      error: localState.error || null,
      hoveredListingZid: hoveredListingZoneIdSelector(state),
      shstore: storeIdSelector(state),
    };
  },
  actionCreators: {
    fetchSeatInventoryListings,
    fetchSelectedListing,
    setListingSelected,
    setPreviousListingSelected,
    fetchSectionStats,
    dispatchFilterChangedHandled,
  },
  reducers: [
    'SEATMAP:LISTING_SELECTED',
    'SEATMAP:LISTING_LOADED',
    'SEATMAP:LISTING_LOADING',
    'SEATMAP:LISTING_ERROR',
    'SEATMAP:ADD_SECTIONIDS',
    'SEATMAP:SECTIONSTATS_LOADING',
    'SEATMAP:SECTIONSTATS_LOADED',
    'SEATMAP:SECTIONSTATS_ERROR',
    'SEATMAP:RESET',
  ],
});

export default controller;
